/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component. Matches the same exemption on use-tree-table.ts. */
import * as React from 'react';

import type { TableBodyHeight } from './table-body-height.js';

/** Breathing room below the container so it doesn't touch the viewport's bottom edge. */
const BOTTOM_GUTTER_PX = 24;

interface UseBoundedTableHeightOptions {
  /** Sibling below the container (e.g. pagination) whose height the viewport cap subtracts. */
  reserveBelowRef?: React.RefObject<HTMLElement | null>;
}

/** 'viewport' measures live rather than summing offsets, since what sits above is page-owned. */
export function useBoundedTableHeight(
  maxBodyHeight: TableBodyHeight | undefined,
  options?: UseBoundedTableHeightOptions,
): {
  containerRef: React.RefObject<HTMLDivElement | null>;
  style: React.CSSProperties | undefined;
  isBounded: boolean;
} {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [top, setTop] = React.useState(0);
  const [reserveBelowPx, setReserveBelowPx] = React.useState(0);
  const tracksViewport = maxBodyHeight === 'viewport';
  const reserveBelowRef = options?.reserveBelowRef;

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!tracksViewport || !el) return;

    const measure = () => {
      setTop(el.getBoundingClientRect().top);
      const belowEl = reserveBelowRef?.current;
      setReserveBelowPx(belowEl ? belowEl.getBoundingClientRect().height : 0);
    };
    measure();

    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(el.offsetParent ?? document.body);
    const belowEl = reserveBelowRef?.current;
    if (belowEl) observer.observe(belowEl);

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [tracksViewport, reserveBelowRef]);

  let style: React.CSSProperties | undefined;
  if (tracksViewport) {
    style = {
      maxHeight: `calc(100vh - ${top}px - ${reserveBelowPx}px - ${BOTTOM_GUTTER_PX}px)`,
    };
  } else if (maxBodyHeight !== undefined) {
    style = { maxHeight: maxBodyHeight };
  }

  return { containerRef, style, isBounded: maxBodyHeight !== undefined };
}
